/**
 * Платформенная авторизация в Hub-embed (CU-869e4mvzp).
 *
 * Hub шлёт в iframe INIT с asellerToken — refresh-токеном пользователя aseller.io.
 * SDK его наружу не отдаёт (useEmbed зеркалит только user/shops/theme/locale),
 * поэтому INIT слушаем сами: origin проверяем экспортируемым из SDK isAllowedOrigin,
 * а сообщения, пришедшие до монтирования React, забираем из буфера early-listener'а.
 *
 * Дальше: refresh → access (POST /api/platform/token) → тот же стор и интерцептор,
 * что у standalone-логина. Keycloak-токен Hub'а (accessToken в INIT) НЕ годится —
 * у плагина свой realm, kid не совпадает с JWKS.
 *
 * NO FALLBACK: нет asellerToken или обмен не удался — анонимными не притворяемся,
 * пишем ошибку в консоль; персональные ручки честно отдадут 401.
 */

import { useEffect } from 'react';
import { getBufferedMessages, isAllowedOrigin } from '@aseller/embed-sdk';
import { exchangePlatformToken } from '../api/platformToken';
import { useAuthStore } from '../stores/authStore';
import { getAllowedOrigins } from '../utils/allowedOrigins';

interface HubInitMessage {
  type: string;
  asellerToken?: string;
  user?: { id?: string };
}

/**
 * Подписка на INIT + обмен токена. Возвращает функцию отписки.
 * Вынесена из хука, чтобы тестироваться без React-рендера.
 */
export function startHubPlatformAuth(): () => void {
  let cancelled = false;
  // Токен, обмен которого уже идёт: INIT приходит и из буфера, и «живьём» —
  // без этого на один токен ушло бы два обмена.
  let inFlight: string | null = null;
  const allowedOrigins = getAllowedOrigins();

  const consume = async (data: unknown): Promise<void> => {
    const message = data as HubInitMessage | null;
    if (!message || message.type !== 'INIT') return;

    const refreshToken = message.asellerToken;
    if (!refreshToken) {
      console.error(
        '[platform-auth] INIT без asellerToken — сохранение/история будут отдавать 401',
      );
      return;
    }

    const { platformRefreshToken } = useAuthStore.getState();
    if (platformRefreshToken === refreshToken || inFlight === refreshToken) return;

    inFlight = refreshToken;
    try {
      const accessToken = await exchangePlatformToken(refreshToken);
      if (cancelled) return;
      useAuthStore.getState().applyPlatformIdentity({
        refreshToken,
        accessToken,
        userId: message.user?.id ?? null,
      });
    } catch (error) {
      console.error('[platform-auth] обмен refresh→access не удался', error);
    } finally {
      if (inFlight === refreshToken) inFlight = null;
    }
  };

  // INIT мог прийти до монтирования React — early-listener SDK его буферизует
  // (origin там уже проверен по тому же списку, см. main.tsx).
  for (const { event } of getBufferedMessages()) void consume(event.data);

  const onMessage = (event: MessageEvent): void => {
    if (!isAllowedOrigin(event.origin, allowedOrigins)) return;
    void consume(event.data);
  };
  window.addEventListener('message', onMessage);

  return () => {
    cancelled = true;
    window.removeEventListener('message', onMessage);
  };
}

export function useHubPlatformAuth(): void {
  useEffect(() => startHubPlatformAuth(), []);
}
